import { useEffect, useRef, useState } from 'react';
import { AppearingFromRight } from 'src/animations/AppearingFromRight';
import styles from './UpgradeDowngradeSection.module.scss';
import { PlanFamily } from 'src/types';
import { CheckoutSeatsSection } from 'src/v2/pages/payment/components/CheckoutSeatsSection';
import {
  useGetDisplayedPlan,
  usePaymentControlState,
  useSubscriptionInfo,
  useSubscriptionPlanById,
} from 'src/hooks';
import { PlanGroup } from 'src/types/wallet/models/PlanGroup';
import { PanelHeader } from 'src/pages/PaymentBetaPage/components/PanelHeader';
import { UpgradeDowngradeSummarySection } from 'src/pages/PaymentBetaPage/components/UpgradeDowngradeSummarySection';
import { UpgradeDowngradeButton } from 'src/pages/PaymentBetaPage/components/UpgradeDowngradeButton';

interface UpgradeDowngradeSectionProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UpgradeDowngradeSection = ({
  isOpen,
  onClose,
}: UpgradeDowngradeSectionProps) => {
  const checkoutContentRef = useRef<HTMLDivElement>(null);
  const [numberOfSeats, setNumberOfSeats] = useState<number>(1);

  const { currentSelectedPlanV2: currentSelectedPlan } = useGetDisplayedPlan();
  const { selectedFamily } = usePaymentControlState();
  const { subscriptionInfo } = useSubscriptionInfo();

  const isUpgradeToTeam = selectedFamily === PlanFamily.BUSINESS;

  const { plan, isPlanLoading } = useSubscriptionPlanById({
    plan_id: currentSelectedPlan?.plan_id,
    seat_count: numberOfSeats,
    shouldSkip: !isUpgradeToTeam,
    withoutReferral: true,
  });

  const isTeams =
    subscriptionInfo?.plan_family === PlanFamily.BUSINESS ||
    subscriptionInfo?.plan_group === PlanGroup.TEAMS;

  useEffect(() => {
    if (isTeams) {
      setNumberOfSeats(subscriptionInfo?.seat_count);
    }
  }, [subscriptionInfo?.seat_count, isTeams]);

  const handleChangeNumberOfSeats = (number: number) => {
    if (number <= 0) {
      setNumberOfSeats(1);
    } else {
      setNumberOfSeats(number);
    }
  };

  const handleCloseSection = () => {
    if (!isTeams) {
      setNumberOfSeats(1);
    }
    onClose();
  };

  const resultPlan = isUpgradeToTeam ? plan : currentSelectedPlan;
  const resultNumberOfSeats = isUpgradeToTeam ? numberOfSeats : undefined;

  return (
    <AppearingFromRight
      width={360}
      inProp={isOpen}
      nodeRef={checkoutContentRef}
    >
      <div className={styles.root}>
        <PanelHeader onClose={handleCloseSection} />

        <div className={styles.mainContainer}>
          {isUpgradeToTeam && (
            <CheckoutSeatsSection
              numberOfSeats={numberOfSeats}
              onChangeNumberOfSeats={handleChangeNumberOfSeats}
              disabled={isTeams}
            />
          )}

          <UpgradeDowngradeSummarySection
            selectedPlan={resultPlan}
            numberOfSeats={resultNumberOfSeats}
            isPlanLoading={isPlanLoading}
          />

          <div className={styles.paymentContainer}>
            <UpgradeDowngradeButton
              selectedPlan={resultPlan}
              numberOfSeats={resultNumberOfSeats}
              isLoading={isUpgradeToTeam ? isPlanLoading : false}
            />
          </div>
        </div>
      </div>
    </AppearingFromRight>
  );
};
